import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import PageHeader from '../../components/common/PageHeader';

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR');
};

const bytesToGb = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return null;
  return number / (1024 ** 3);
};

const getLicenseLabel = (license = {}, licenseNameBySku = {}) => {
  if (typeof license === 'string') return licenseNameBySku[license] || license;
  return license.skuLicense || license.licenseName || license.skuPartNumber || license.name || licenseNameBySku[license.skuId] || license.skuId || 'Licence';
};

function useClientOrganizationNames() {
  const { data: organizations = [] } = useSelector((state) => state.organizations.organizations);
  return useMemo(
    () => organizations.map((organization) => organization.name).filter(Boolean),
    [organizations],
  );
}

export default function ClientO365Users() {
  const organizationNames = useClientOrganizationNames();
  const { data: o365Users = [], loading, error } = useSelector((state) => state.o365.users);
  const { data: o365Licenses = [] } = useSelector((state) => state.o365.licenses);

  const clientName = organizationNames[0] || 'Client';
  const users = useMemo(() => {
    const hasOrganizationName = o365Users.some((user) => user.organizationName);
    if (!organizationNames.length || !hasOrganizationName) return o365Users;
    return o365Users.filter((user) => organizationNames.includes(user.organizationName));
  }, [o365Users, organizationNames]);

  const licenseNameBySku = useMemo(() => o365Licenses.reduce((acc, license) => {
    const label = license.licenseName || license.name || license.skuPartNumber;
    [license.skuId, license.id, license.licenseId, license.skuPartNumber, license.licenseName]
      .filter(Boolean)
      .forEach((key) => { acc[key] = label || key; });
    return acc;
  }, {}), [o365Licenses]);

  const columns = useMemo(() => [
    {
      accessorFn: (row) => row.displayName || row.userPrincipalName || 'Utilisateur',
      id: 'displayName',
      header: 'Nom',
      size: 240,
      filterVariant: 'autocomplete',
      Cell: ({ renderedCellValue }) => <Typography sx={{ fontWeight: 700 }}>{renderedCellValue}</Typography>,
    },
    {
      accessorKey: 'userPrincipalName',
      header: 'Email',
      size: 280,
    },
    {
      accessorFn: (row) => Array.isArray(row.assignedLicenses)
        ? row.assignedLicenses.map((license) => getLicenseLabel(license, licenseNameBySku)).join(' ')
        : '',
      id: 'assignedLicenses',
      header: 'Licences associées',
      size: 360,
      Cell: ({ row }) => {
        const licenses = Array.isArray(row.original.assignedLicenses) ? row.original.assignedLicenses : [];
        if (!licenses.length) return <Typography variant="body2" color="text.secondary">Aucune</Typography>;
        return (
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {licenses.map((license, index) => (
              <Chip
                key={`${getLicenseLabel(license, licenseNameBySku)}-${index}`}
                label={getLicenseLabel(license, licenseNameBySku)}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        );
      },
    },
    {
      accessorFn: (row) => formatDate(row.created),
      id: 'created',
      header: 'Créé le',
      size: 130,
    },
    {
      accessorFn: (row) => row.deleted ? 'Supprimé' : 'Actif',
      id: 'status',
      header: 'Statut',
      size: 120,
      filterVariant: 'autocomplete',
      Cell: ({ row }) => (
        <Chip size="small" color={row.original.deleted ? 'error' : 'success'} label={row.original.deleted ? 'Supprimé' : 'Actif'} />
      ),
    },
    {
      accessorFn: (row) => {
        const used = bytesToGb(row.mailboxStorageUsed);
        return used === null ? '' : used.toFixed(2);
      },
      id: 'mailboxStorageUsed',
      header: 'Mailbox',
      size: 220,
      Cell: ({ row }) => {
        const used = bytesToGb(row.original.mailboxStorageUsed);
        const limit = bytesToGb(row.original.mailboxStorageLimit);
        if (used === null || limit === null) return <Typography variant="body2" color="text.secondary">N/A</Typography>;
        const percent = Math.min((used / limit) * 100, 100);
        return (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 180 }}>
            <LinearProgress
              variant="determinate"
              value={percent}
              color={percent >= 90 ? 'error' : percent >= 75 ? 'warning' : 'primary'}
              sx={{ height: 8, borderRadius: 5, flex: 1 }}
            />
            <Typography variant="caption" color="text.secondary">{used.toFixed(1)} / {limit.toFixed(1)} Go</Typography>
          </Stack>
        );
      },
    },
  ], [licenseNameBySku]);

  const table = useMaterialReactTable({
    columns,
    data: users,
    localization: MRT_Localization_FR,
    enableColumnFilterModes: true,
    enableColumnFilters: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableGlobalFilter: true,
    initialState: {
      density: 'compact',
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 50 },
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      placeholder: 'Rechercher',
    },
    muiPaginationProps: {
      rowsPerPageOptions: [25, 50, 100],
      shape: 'rounded',
      variant: 'outlined',
    },
    paginationDisplayMode: 'pages',
    positionGlobalFilter: 'right',
  });

  return (
    <Box>
      <PageHeader section={clientName} title="Vos utilisateurs" sx={{ my: 1, mb: 3 }} />
      {error ? <Alert severity="error" sx={{ mb: 2 }}>Erreur de chargement des utilisateurs O365.</Alert> : null}
      {loading ? (
        <Skeleton variant="rectangular" width="100%" height={520} />
      ) : users.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" color="text.secondary">Aucun utilisateur O365 trouvé</Typography>
        </Box>
      ) : (
        <MaterialReactTable table={table} />
      )}
    </Box>
  );
}
